/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-floating-promises, @typescript-eslint/no-misused-promises */
import { useState } from 'react'
import { createFileRoute, Link, Outlet, useMatches, useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  ArrowLeft, Trash2, MapPin, User, Building2, Tag,
  FileText, StickyNote, XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { jobsAPI } from '@/lib/api/jobs'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

function StatusBadge({ title, color }: { title?: string; color?: string | null }) {
  const { t } = useTranslation()
  if (!title) return <Badge variant="outline">{t('jobDetail:unknown')}</Badge>
  const bg = color ? { backgroundColor: color, color: '#fff' } : undefined
  return <Badge style={bg} variant={bg ? 'default' : 'secondary'}>{title}</Badge>
}

function formatDateTime(d: string | null | undefined) {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const JOB_TABS = [
  { to: 'overview', segment: 'overview', label: 'jobDetail:overview', icon: FileText },
  { to: 'notes', segment: 'notes', label: 'jobDetail:notesInstructions', icon: StickyNote },
] as const

export const Route = createFileRoute('/_authenticated/jobs/$jobId')({
  component: JobDetailLayout,
})

function JobDetailLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { jobId } = useParams({ from: '/_authenticated/jobs/$jobId' })
  const queryClient = useQueryClient()
  const [deleteOpen, setDeleteOpen] = useState(false)
  const matches = useMatches()
  const activeTab = matches[matches.length - 1]?.routeId?.split('/').pop() ?? 'overview'

  const { data: job, isLoading, error } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => jobsAPI.get(jobId),
  })

  const deleteMutation = useMutation({
    mutationFn: () => jobsAPI.delete(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      navigate({ to: '/jobs' })
    },
  })

  const j = job as any

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <motion.div
          className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    )
  }

  if (error || !j) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <XCircle className="w-10 h-10 text-destructive" />
        <p className="text-destructive font-medium">{t('jobDetail:jobNotFound')}</p>
        <Button variant="outline" onClick={() => navigate({ to: '/jobs' })}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Jobs
        </Button>
      </div>
    )
  }

  const statusTitle = j.status_title || j.status?.title || 'Unknown'
  const statusColor = j.status_color || j.status?.color || null
  const subStatusTitle = j.sub_status_title || j.subStatus?.title
  const categoryTitle = j.category_title || j.category?.title || '—'
  const customerName = j.customer_name || j.customer?.name || '—'
  const contactName = [j.contact_first_name, j.contact_last_name].filter(Boolean).join(' ') || j.contact?.name || '—'
  const locationCity = j.location_city || j.location?.city || ''
  const locationAddress = j.location_address || j.location?.address || '—'

  return (
    <motion.div
      className="space-y-6 pb-8"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" className="mt-1 shrink-0" onClick={() => navigate({ to: '/jobs' })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">Job #{j.job_number || j.jobNumber}</h1>
              <StatusBadge title={statusTitle} color={statusColor} />
              {subStatusTitle && <StatusBadge title={subStatusTitle} color={j.sub_status_color} />}
              {j.two_tech && <Badge variant="outline" className="text-amber-600 border-amber-300">2-Tech</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              Created {formatDateTime(j.created_at || j.createdAt)}
              {j.old_job_number && <span className="ml-2 text-xs">(V2: {j.old_job_number})</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="text-destructive hover:text-destructive">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Customer', value: customerName, icon: Building2 },
          { label: 'Category', value: categoryTitle, icon: Tag },
          { label: 'Contact', value: contactName, icon: User },
          { label: 'Location', value: locationCity || locationAddress, icon: MapPin },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <Card className="p-3">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <stat.icon className="w-3.5 h-3.5" />
                <span className="text-xs">{stat.label}</span>
              </div>
              <p className="text-sm font-medium truncate">{stat.value}</p>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {JOB_TABS.map(tab => (
          <Link
            key={tab.to}
            to={`/jobs/${jobId}/${tab.to}`}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.segment
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <tab.icon className="w-4 h-4" />
            {t(tab.label)}
          </Link>
        ))}
      </div>

      <Outlet />

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Job #{j.job_number || j.jobNumber}?</DialogTitle>
            <DialogDescription>{t('jobDetail:thisWillSoftdeleteTheJobItCanBeRestoredL')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}
